import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dids } from '@/api/dids';
import type { DidRow } from '@/api/dids';
import { carriers } from '@/api/carriers';
import { ivrTrees } from '@/api/ivrTrees';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { CreateDidDialog } from './CreateDidDialog';
import { AssignDidDialog } from './AssignDidDialog';
import { formatE164 } from './utils';
import { formatRef } from '@/lib/formatRef';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/cn';
import { Plus, MoreHorizontal, Trash2, Link2, Link2Off } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

// ── Role constants ─────────────────────────────────────────────────────────────

const CAN_ASSIGN = ['super_admin', 'reseller_owner', 'office_manager'];
const CAN_DELETE = ['super_admin'];

// ── Coverage badge ─────────────────────────────────────────────────────────────

function CoverageBadge({ coverage }: { coverage: DidRow['coverage'] }) {
  const styles: Record<string, string> = {
    us: 'bg-blue-50 text-blue-700 border-blue-200',
    ca: 'bg-red-50 text-red-700 border-red-200',
    international: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  const labels: Record<string, string> = {
    us: 'US',
    ca: 'CA',
    international: 'Intl',
  };
  return (
    <span
      className={cn(
        'inline-block rounded border px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider leading-none',
        styles[coverage] ?? 'bg-slate-50 text-slate-500 border-slate-200',
      )}
    >
      {labels[coverage] ?? coverage}
    </span>
  );
}

// ── Tree name cell — lazy resolve ─────────────────────────────────────────────

function TreeCell({ treeId }: { treeId: string | null }) {
  const { data: tree } = useQuery({
    queryKey: ['ivrTree', treeId],
    queryFn: () => ivrTrees.get(treeId!),
    enabled: !!treeId,
  });
  if (!treeId) return <span className="text-slate-400">—</span>;
  if (!tree) return <span className="font-mono text-xs text-slate-400">…</span>;
  return (
    <a
      href={`/ivr/trees/${tree.id}`}
      onClick={(e) => e.stopPropagation()}
      className="text-sm text-primary hover:underline"
    >
      {tree.name}
    </a>
  );
}

// ── Carrier name cell — lazy resolve ─────────────────────────────────────────

function CarrierCell({ carrierId }: { carrierId: string | null }) {
  const { data: carrier } = useQuery({
    queryKey: ['carrier', carrierId],
    queryFn: () => carriers.get(carrierId!),
    enabled: !!carrierId,
    staleTime: 60_000,
  });
  if (!carrierId) return <span className="text-slate-400">—</span>;
  if (!carrier) return <span className="font-mono text-xs text-slate-400">…</span>;
  return (
    <span className="font-mono text-xs text-slate-700">{carrier.name}</span>
  );
}

// ── Active toggle ─────────────────────────────────────────────────────────────

function ActiveToggle({ row, canMutate }: { row: DidRow; canMutate: boolean }) {
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: () => dids.update(row.id, { active: !row.active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dids'] }),
  });
  return (
    <Switch
      checked={row.active}
      onCheckedChange={() => toggle.mutate()}
      disabled={!canMutate || toggle.isPending}
      aria-label={row.active ? 'Deactivate DID' : 'Activate DID'}
    />
  );
}

// ── Release confirm dialog ────────────────────────────────────────────────────

function ReleaseDialog({ row, onClose }: { row: DidRow; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const release = useMutation({
    // `null` (not undefined) — JSON.stringify DROPS undefined keys, which
    // made this button a silent no-op since it shipped (found 2026-07-02).
    // null explicitly clears the assignment; the backend keeps reseller_id
    // so the number returns to the reseller's pool.
    mutationFn: () =>
      dids.update(row.id, { customer_id: null, routing_target_id: null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dids'] });
      toast({ title: t('dids.releaseTitle'), description: `${formatE164(row.e164)} is now unassigned.` });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('dids.releaseTitle')}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          {t('dids.releaseBody', { number: formatE164(row.e164) })}
        </p>
        {release.isError && (
          <p role="alert" className="text-xs text-red-500">
            {release.error instanceof Error ? release.error.message : t('dids.releaseFailed')}
          </p>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={release.isPending}>{t('common.cancel')}</Button>
          </DialogClose>
          <Button variant="destructive" disabled={release.isPending} onClick={() => release.mutate()}>
            {release.isPending ? t('dids.releasing') : t('dids.release')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete confirm dialog ─────────────────────────────────────────────────────

function DeleteDialog({ row, onClose }: { row: DidRow; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: () => dids.remove(row.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dids'] });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('dids.deleteTitle')}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          {t('dids.deleteBody', { number: formatE164(row.e164) })}
        </p>
        {del.isError && (
          <p role="alert" className="text-xs text-red-500">
            {del.error instanceof Error ? del.error.message : t('dids.deleteFailed')}
          </p>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={del.isPending}>{t('common.cancel')}</Button>
          </DialogClose>
          <Button variant="destructive" disabled={del.isPending} onClick={() => del.mutate()}>
            {del.isPending ? t('common.deleting') : t('common.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function DidsList() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const qc = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [assignDid, setAssignDid] = useState<DidRow | null>(null);
  const [releaseDid, setReleaseDid] = useState<DidRow | null>(null);
  const [deleteDid, setDeleteDid] = useState<DidRow | null>(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [assignedFilter, setAssignedFilter] = useState<'all' | 'assigned' | 'unassigned'>('all');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isSuperAdmin = user.is_super_admin;
  const canAssign = CAN_ASSIGN.includes(user.role);
  const canDelete = CAN_DELETE.includes(user.role);

  function handleSearchChange(v: string) {
    setSearch(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(v), 300);
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['dids', debouncedSearch, assignedFilter],
    queryFn: () =>
      dids.list({
        search: debouncedSearch || undefined,
        assigned: assignedFilter,
      }),
  });

  const rows = data?.rows ?? [];

  const columns = [
    {
      header: t('dids.columnRef'),
      cell: (row: DidRow) => (
        <span className="font-mono text-xs text-slate-500">
          {row.ref_number != null ? formatRef(row.ref_number, 'DID') : '—'}
        </span>
      ),
    },
    {
      header: t('dids.columnE164'),
      cell: (row: DidRow) => (
        <span className="font-mono text-sm tracking-wide">{formatE164(row.e164)}</span>
      ),
    },
    {
      header: t('dids.columnCoverage'),
      cell: (row: DidRow) => <CoverageBadge coverage={row.coverage} />,
    },
    {
      header: t('dids.columnCustomer'),
      cell: (row: DidRow) =>
        row.customer_id ? (
          // Name + human account number — never the raw UUID (rule 28).
          <span className="inline-flex items-center gap-1.5 text-sm text-slate-700">
            <span className="truncate max-w-[160px]">{row.customer_name ?? '—'}</span>
            {row.customer_number != null && (
              <span className="rounded border border-slate-200 bg-slate-50 px-1 py-0.5 font-mono text-[10px] text-slate-500">
                C-{formatRef(row.customer_number)}
              </span>
            )}
          </span>
        ) : (
          <span className="inline-flex items-center rounded border border-dashed border-slate-300 px-1.5 py-0.5 text-[11px] text-slate-400">
            {t('dids.unassigned')}
          </span>
        ),
    },
    // Which reseller's pool the number sits in — platform admins juggle
    // several resellers, so name it explicitly (super_admin only; a
    // reseller viewing the page only ever sees their own pool).
    ...(isSuperAdmin ? [{
      header: 'Reseller',
      cell: (row: DidRow) =>
        row.reseller_id ? (
          <span className="inline-flex items-center gap-1.5 text-sm text-slate-700">
            <span className="truncate max-w-[140px]">{row.reseller_name ?? '—'}</span>
            {row.reseller_number != null && (
              <span className="rounded border border-slate-200 bg-slate-50 px-1 py-0.5 font-mono text-[10px] text-slate-500">
                R-{formatRef(row.reseller_number)}
              </span>
            )}
          </span>
        ) : (
          <span className="inline-flex items-center rounded border border-dashed border-slate-300 px-1.5 py-0.5 text-[11px] text-slate-400">
            Platform pool
          </span>
        ),
    }] : []),
    {
      header: t('dids.columnTree'),
      cell: (row: DidRow) => (
        <TreeCell
          treeId={row.routing_kind === 'ivr' ? (row.routing_target_id ?? null) : null}
        />
      ),
    },
    {
      header: t('dids.columnCarrier'),
      cell: (row: DidRow) => <CarrierCell carrierId={row.carrier_id ?? null} />,
    },
    {
      header: t('dids.columnActive'),
      cell: (row: DidRow) => (
        <ActiveToggle row={row} canMutate={canAssign} />
      ),
    },
    {
      header: '',
      className: 'w-10 text-end',
      cell: (row: DidRow) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t('common.actions')}
              onClick={(e) => e.stopPropagation()}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            {canAssign && (
              <DropdownMenuItem onClick={() => setAssignDid(row)}>
                <Link2 className="h-3.5 w-3.5 mr-2" />
                {t('dids.assign')}
              </DropdownMenuItem>
            )}
            {canAssign && row.customer_id && (
              <DropdownMenuItem onClick={() => setReleaseDid(row)}>
                <Link2Off className="h-3.5 w-3.5 mr-2" />
                {t('dids.release')}
              </DropdownMenuItem>
            )}
            {canDelete && (
              <DropdownMenuItem
                className="text-red-600 focus:text-red-600 focus:bg-red-50"
                onClick={() => setDeleteDid(row)}
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" />
                {t('common.delete')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('dids.title')}
        description={t('dids.description')}
        actions={
          <div className="flex items-center gap-2">
            <Select
              value={assignedFilter}
              onValueChange={(v) => setAssignedFilter(v as typeof assignedFilter)}
            >
              <SelectTrigger className="h-9 w-40 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('dids.allNumbers')}</SelectItem>
                <SelectItem value="assigned">{t('dids.assigned')}</SelectItem>
                <SelectItem value="unassigned">{t('dids.unassigned')}</SelectItem>
              </SelectContent>
            </Select>
            <Input
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t('dids.searchPlaceholder')}
              className="h-9 w-48 font-mono"
              aria-label={t('dids.searchPlaceholder')}
            />
            {isSuperAdmin && (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-1.5" />
                {t('dids.addDid')}
              </Button>
            )}
          </div>
        }
      />

      {isLoading ? (
        <div className="text-sm text-slate-400">{t('common.loading')}</div>
      ) : error ? (
        <div role="alert" className="text-sm text-red-500">
          {error instanceof Error ? error.message : t('dids.failedToLoad')}
          <Button
            variant="outline"
            size="sm"
            className="ms-3"
            onClick={() => qc.invalidateQueries({ queryKey: ['dids'] })}
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          empty={
            debouncedSearch
              ? t('dids.noMatch', { query: debouncedSearch })
              : assignedFilter === 'unassigned'
                ? t('dids.noUnassigned')
                : t('dids.noProvisioned')
          }
        />
      )}

      <CreateDidDialog open={createOpen} onOpenChange={setCreateOpen} />
      {assignDid && (
        <AssignDidDialog
          did={assignDid}
          open
          onOpenChange={(v) => { if (!v) setAssignDid(null); }}
        />
      )}
      {releaseDid && (
        <ReleaseDialog row={releaseDid} onClose={() => setReleaseDid(null)} />
      )}
      {deleteDid && (
        <DeleteDialog row={deleteDid} onClose={() => setDeleteDid(null)} />
      )}
    </>
  );
}
